/**
 * Auto-scrolling merchant logo ribbon — iteration 36d.
 *
 * NOTE: These are HAND-CRAFTED SVG PLACEHOLDER MARKS for display until the
 * legal-approved merchant logos land. Each mark uses a unique geometry +
 * accent color so the ribbon reads as real logos, not text chips. To swap in
 * a real logo, replace the entry's `svg` render function with the merchant's
 * approved SVG (keep viewBox="0 0 40 40" for consistent sizing).
 */
import React from "react";

// prettier-ignore
const BRANDS = [
  { name: "Loftrey",       accent: "#0891b2", svg: (c) => (<><rect x="6" y="14" width="28" height="12" rx="2" fill={c}/><rect x="10" y="20" width="20" height="4" fill="#fff" opacity=".5"/></>) },
  { name: "Northlane",     accent: "#0f766e", svg: (c) => (<polygon points="20,6 34,34 6,34" fill={c}/>) },
  { name: "Meridian & Co", accent: "#7c3aed", svg: (c) => (<><circle cx="20" cy="20" r="14" fill="none" stroke={c} strokeWidth="3"/><line x1="6" y1="20" x2="34" y2="20" stroke={c} strokeWidth="3"/></>) },
  { name: "Petalcraft",    accent: "#db2777", svg: (c) => (<><circle cx="20" cy="12" r="6" fill={c}/><circle cx="12" cy="24" r="6" fill={c} opacity=".7"/><circle cx="28" cy="24" r="6" fill={c} opacity=".7"/></>) },
  { name: "Halo Wear",     accent: "#0284c7", svg: (c) => (<><circle cx="20" cy="20" r="12" fill="none" stroke={c} strokeWidth="3"/><circle cx="20" cy="20" r="4" fill={c}/></>) },
  { name: "Basilico",      accent: "#16a34a", svg: (c) => (<path d="M20 6 C 10 14, 10 26, 20 34 C 30 26, 30 14, 20 6 Z" fill={c}/>) },
  { name: "Bright Foundry",accent: "#ea580c", svg: (c) => (<><rect x="8" y="8" width="10" height="10" fill={c}/><rect x="22" y="8" width="10" height="10" fill={c} opacity=".6"/><rect x="8" y="22" width="10" height="10" fill={c} opacity=".6"/><rect x="22" y="22" width="10" height="10" fill={c}/></>) },
  { name: "Sunreach",      accent: "#f59e0b", svg: (c) => (<><circle cx="20" cy="20" r="6" fill={c}/><g stroke={c} strokeWidth="2.5" strokeLinecap="round"><line x1="20" y1="4"  x2="20" y2="10"/><line x1="20" y1="30" x2="20" y2="36"/><line x1="4"  y1="20" x2="10" y2="20"/><line x1="30" y1="20" x2="36" y2="20"/></g></>) },
  { name: "Voltera",       accent: "#4f46e5", svg: (c) => (<polygon points="22,4 12,22 20,22 18,36 30,16 22,16" fill={c}/>) },
  { name: "Curio Lab",     accent: "#0d9488", svg: (c) => (<><rect x="12" y="10" width="16" height="20" rx="3" fill="none" stroke={c} strokeWidth="2.5"/><line x1="12" y1="16" x2="28" y2="16" stroke={c} strokeWidth="2.5"/><circle cx="20" cy="24" r="3" fill={c}/></>) },
  { name: "Ivy Grocers",   accent: "#65a30d", svg: (c) => (<path d="M8 20 Q 20 6 32 20 Q 20 34 8 20 Z" fill={c}/>) },
  { name: "Harbourwood",   accent: "#334155", svg: (c) => (<><path d="M6 30 L20 8 L34 30 Z" fill={c}/><rect x="17" y="22" width="6" height="8" fill="#fff" opacity=".7"/></>) },
  { name: "The Stack",     accent: "#0369a1", svg: (c) => (<><rect x="6" y="10" width="28" height="5" rx="1" fill={c}/><rect x="6" y="17" width="28" height="5" rx="1" fill={c} opacity=".7"/><rect x="6" y="24" width="28" height="5" rx="1" fill={c} opacity=".5"/></>) },
  { name: "Motif Beauty",  accent: "#be185d", svg: (c) => (<><circle cx="14" cy="20" r="8" fill="none" stroke={c} strokeWidth="2.5"/><circle cx="26" cy="20" r="8" fill="none" stroke={c} strokeWidth="2.5"/></>) },
  { name: "Wren Bikes",    accent: "#059669", svg: (c) => (<><circle cx="12" cy="26" r="6" fill="none" stroke={c} strokeWidth="2.5"/><circle cx="28" cy="26" r="6" fill="none" stroke={c} strokeWidth="2.5"/><line x1="12" y1="26" x2="20" y2="14" stroke={c} strokeWidth="2.5"/><line x1="20" y1="14" x2="28" y2="26" stroke={c} strokeWidth="2.5"/></>) },
];

function LogoMark({ brand }) {
  return (
    <svg viewBox="0 0 40 40" width="24" height="24" aria-hidden="true" className="shrink-0">
      {brand.svg(brand.accent)}
    </svg>
  );
}

export default function LogoRibbon() {
  const track = [...BRANDS, ...BRANDS];
  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 py-14" data-testid="merchant-logo-ribbon">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Trusted by 1,240+ brands across North America</div>
      </div>
      <div className="mt-6 relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
        <div className="flex gap-8 whitespace-nowrap animate-logo-marquee hover:[animation-play-state:paused]">
          {track.map((b, i) => (
            <div
              key={`${b.name}-${i}`}
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-2xl bg-white/85 backdrop-blur border border-slate-200 shrink-0 shadow-sm"
              data-testid={`logo-chip-${i}`}
            >
              <LogoMark brand={b} />
              <span className="text-sm font-display font-medium text-slate-800 tracking-tight">{b.name}</span>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes logo-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-logo-marquee {
          animation: logo-marquee 45s linear infinite;
          width: max-content;
        }
      `}</style>
    </section>
  );
}
